import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cropper from "react-easy-crop";
import WeekPicker from "../components/WeekPicker";
import { uploadImageToCloudflare } from "../../utils/cloudflareUpload";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";
import { SPORTS, AWARDS, DIVISIONS, LEVELS_GRADE, LEVELS_AGE, LEVELS_SCHOOL, SEASONS } from "../data/standoutsConstants";
import OrgSearchDropdown from "../components/OrgSearchDropdown";
import CityAutocomplete from "../components/CityAutocomplete";
import "../styles/SubmitForm.css";
import "../styles/StandoutDetail.css";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92));
}
const FALLBACK_IMG = "https://imagedelivery.net/P2N4CYIMf6U7zWq6K2L_RA/012b73aa-f130-4668-a384-7cd50b6dbb00/public";

const TIPS = [
  {
    svg: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    ),
    title: "Use clear photos",
    desc: "Action shots or portraits work best.",
  },
  {
    svg: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: "Highlight key stats",
    desc: "Focus on the top 1–3 stats that show impact.",
  },
  {
    svg: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    title: "Be accurate",
    desc: "Double-check names, teams, and stats before submitting.",
  },
  {
    svg: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: "Submit early",
    desc: "Don't miss the deadline — submit by Sunday. Standouts are announced every Monday.",
  },
];

// Helper function to get the next Monday
const getNextMonday = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysUntilMonday);

  // Format as yyyy-MM-dd for date input
  const year = nextMonday.getFullYear();
  const month = String(nextMonday.getMonth() + 1).padStart(2, '0');
  const day = String(nextMonday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date range
const getDateRange = (startDate) => {
  if (!startDate) return '';
  const start = new Date(startDate);
  const end = new Date(start);
  end.setDate(start.getDate() + 6); // Add 6 days for a full week

  const formatDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  return `${formatDate(start)}–${formatDate(end)}, ${start.getFullYear()}`;
};

const INITIAL_FORM = {
  week: "",
  startDate: getNextMonday(),
  dateRange: getDateRange(getNextMonday()),
  season_name: "Spring",
  season_year: 2026,
  orgName: "",
  yourName: "",
  email: "",
  athleteName: "",
  teamName: "",
  sport: "Flag Football",
  level: "12U",
  division: "Boys",
  award: "MVP",
  stat1: "2",
  label1: "TDs",
  stat2: "1",
  label2: "INT",
  stat3: "9",
  label3: "COMP",
  image: null,
};

const isSubmissionLocked = () => {
  const now = new Date();
  const day = now.getDay();
  const hours = now.getHours();
  if (day === 0 && hours >= 22) return true;
  if (day === 1 && hours < 5) return true;
  return false;
};

const EMPTY_REQUEST = {
  name: '', sport: 'Flag Football', city: '', state: '',
  contact_name: '', contact_email: '', phone: '', website: '',
};

export default function SubmitStandout() {
  const navigate = useNavigate();
  const { submitStandout, requestOrganization, getOrgAthletes, getApprovedOrganizations } = useAuth();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/standouts/login');
  };

  // Auth and approval state
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userApproved, setUserApproved] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [confirmPermission, setConfirmPermission] = useState(false);
  const [confirmAuthorized, setConfirmAuthorized] = useState(false);

  // Org selection step
  const [orgStep, setOrgStep] = useState('select'); // 'select' | 'request' | 'pending' | 'form'
  const [orgTab, setOrgTab] = useState('my'); // 'my' | 'pending' | 'all'
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [joinSuccessOrg, setJoinSuccessOrg] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);

  // Athlete search state
  const [existingAthletes, setExistingAthletes] = useState([]);
  const [athleteQuery, setAthleteQuery] = useState('');
  const [athleteDropdownOpen, setAthleteDropdownOpen] = useState(false);
  const athleteDropdownRef = useRef(null);

  // Check authentication and approval on mount
  useEffect(() => {
    checkAuthAndApproval();
    fetchAllOrganizations();
  }, []);

  const fetchAllOrganizations = async () => {
    const result = await getApprovedOrganizations();
    if (result.success) {
      setAllOrganizations(result.data);
    }
  };

  const checkAuthAndApproval = async () => {
    try {
      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Not logged in - allow them to browse organizations
        setAuthLoading(false);
        setOrgTab('all'); // Set default tab to 'all' for non-logged-in users
        return;
      }

      setCurrentUser(user);

      // Check user approval status and organizations
      const { data: approvalData, error: approvalError } = await supabase
        .rpc('check_user_approval', { p_user_id: user.id });

      if (approvalError) {
        console.error('Approval check error:', approvalError);
        setAuthLoading(false);
        return;
      }

      if (!approvalData || approvalData.length === 0) {
        // No role found - redirect to signup
        navigate('/standouts/signup');
        return;
      }

      const { approved, role, has_organizations, acknowledged } = approvalData[0];

      if (!approved) {
        // Not approved - redirect to pending page
        navigate('/standouts/pending-approval');
        return;
      }

      // Approved but hasn't seen the one-time "Account Approved" screen yet
      if (!acknowledged) {
        navigate('/standouts/account-approved');
        return;
      }

      // User is approved
      setUserApproved(true);
      setUserRole(role);

      // Get user's profile information
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('id', user.id)
        .single();

      // Determine user name and email with fallbacks
      let fullName = '';
      let userEmail = user.email || '';

      if (!profileError && profileData) {
        setUserProfile(profileData);
        fullName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim();
        userEmail = profileData.email || user.email || '';
      }

      // Fallback to auth metadata if profile data is incomplete
      if (!fullName && user.user_metadata) {
        if (user.user_metadata.full_name) {
          fullName = user.user_metadata.full_name;
        } else if (user.user_metadata.first_name || user.user_metadata.last_name) {
          fullName = `${user.user_metadata.first_name || ''} ${user.user_metadata.last_name || ''}`.trim();
        }
      }

      // Set default if still no name
      if (!fullName) {
        fullName = userEmail.split('@')[0] || 'User';
      }

      // Auto-populate form with user's information
      setForm((prev) => ({
        ...prev,
        yourName: fullName,
        email: userEmail,
      }));

      console.log('User profile loaded:', { fullName, userEmail }); // Debug log

      // Get user's organizations
      const { data: orgsData, error: orgsError } = await supabase
        .rpc('get_user_organizations', { p_user_id: user.id });

      if (!orgsError && orgsData) {
        setUserOrganizations(orgsData);
      }

      setAuthLoading(false);
    } catch (error) {
      console.error('Auth check error:', error);
      setAuthLoading(false);
      // Don't redirect on error - allow browsing organizations
      setOrgTab('all');
    }
  };

  useEffect(() => {
    const close = (e) => {
      if (athleteDropdownRef.current && !athleteDropdownRef.current.contains(e.target)) {
        setAthleteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
  };

  const handleOrgContinue = (org = null) => {
    // Require login to continue
    if (!currentUser) {
      navigate('/standouts/login');
      return;
    }

    const orgToUse = org || selectedOrg;
    if (!orgToUse) return;

    setSelectedOrg(orgToUse);
    setForm((prev) => ({
      ...prev,
      orgName: orgToUse.name,
      sport: orgToUse.sport || prev.sport,
    }));
    // Load existing athletes for this org
    const slug = orgToUse.slug || orgToUse.name?.toLowerCase().replace(/\s+/g, '-');
    if (slug) {
      getOrgAthletes(slug, currentUser?.id).then((r) => {
        if (r.success) setExistingAthletes(r.data);
      });
    }
    setOrgStep('form');
  };

  const handleRequestToJoin = async (org) => {
    // Require login to request joining
    if (!currentUser) {
      navigate('/standouts/login');
      return;
    }

    try {
      setRequestSubmitting(true);
      setRequestError(null);

      // Create user_organizations record
      const { error } = await supabase
        .from('user_organizations')
        .insert([{
          user_id: currentUser.id,
          organization_id: org.id,
          status: 'pending',
          request_type: 'join_existing',
        }]);

      if (error) {
        // Check if already requested
        if (error.code === '23505') {
          setRequestError('You have already requested to join this organization.');
        } else {
          throw error;
        }
      } else {
        // Reload user organizations
        await checkAuthAndApproval();
        setSelectedOrg(null);
        setJoinSuccessOrg(org.name);
      }
    } catch (error) {
      console.error('Request to join error:', error);
      setRequestError(error.message || 'Failed to send request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleOrgRequest = async () => {
    if (!requestForm.name.trim() || !requestForm.contact_name.trim() || !requestForm.contact_email.trim()) {
      setRequestError('Organization Name, Contact Name and Email are required.');
      return;
    }
    setRequestSubmitting(true);
    setRequestError(null);

    try {
      // Create organization
      const result = await requestOrganization(requestForm);

      if (!result.success) {
        setRequestError(result.error || 'Request failed. Please try again.');
        return;
      }

      // Get the created organization
      const { data: newOrg, error: fetchError } = await supabase
        .from('organizations')
        .select('id')
        .eq('name', requestForm.name.trim())
        .single();

      if (!fetchError && newOrg && currentUser) {
        // Create user_organizations record for this new org
        await supabase
          .from('user_organizations')
          .insert([{
            user_id: currentUser.id,
            organization_id: newOrg.id,
            status: 'pending',
            request_type: 'create_new',
          }]);
      }

      setOrgStep('pending');
    } catch (error) {
      console.error('Organization request error:', error);
      setRequestError(error.message || 'Request failed. Please try again.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const imageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setRawImageSrc(localUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropModalOpen(true);
  };

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCropDone = async () => {
    if (!croppedAreaPixels) return;
    setUploadingPhoto(true);
    setUploadError(null);
    setCropModalOpen(false);
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const croppedFile = new File([blob], "athlete.jpg", { type: "image/jpeg" });
      const localPreview = URL.createObjectURL(blob);
      setForm((prev) => ({ ...prev, image: localPreview }));
      const cloudflareUrl = await uploadImageToCloudflare(croppedFile);
      setForm((prev) => ({ ...prev, image: cloudflareUrl }));
    } catch (err) {
      setUploadError("Photo upload failed. Please try again.");
      setForm((prev) => ({ ...prev, image: null }));
    } finally {
      setUploadingPhoto(false);
      setRawImageSrc(null);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const previewStats = [
    { v: form.stat1, l: form.label1 },
    { v: form.stat2, l: form.label2 },
    { v: form.stat3, l: form.label3 },
  ].filter((s) => s.v.trim());

  const handleSubmit = async () => {
    if (uploadingPhoto || submitting) return;
    setSubmitError(null);

    if (!form.orgName.trim() || !form.yourName.trim() || !form.email.trim() ||
        !form.athleteName.trim() || !form.teamName.trim() || !form.sport.trim() ||
        !form.level.trim() || !form.division.trim() || !form.season_name.trim() || !form.season_year) {
      setSubmitError('Please fill in all required fields.');
      return;
    }

    const stats = [
      { value: form.stat1, label: form.label1 },
      { value: form.stat2, label: form.label2 },
      { value: form.stat3, label: form.label3 },
    ].filter((s) => s.value && s.value.toString().trim());

    setSubmitting(true);
    try {
      const orgLocation = selectedOrg
        ? (selectedOrg.city && selectedOrg.state
            ? `${selectedOrg.city}, ${selectedOrg.state}`
            : selectedOrg.city || selectedOrg.state || selectedOrg.org_location || '')
        : '';

      const result = await submitStandout({
        submitter_name: form.yourName,
        submitter_email: form.email,
        org_name: form.orgName,
        org_location: orgLocation,
        athlete_name: form.athleteName,
        team_name: form.teamName,
        sport: form.sport,
        level: form.level,
        division: form.division,
        award: form.award,
        stats,
        photo_url: form.image,
        week_label: form.week ? `Week ${form.week}` : null,
        week_date_range: form.dateRange || null,
        season: `${form.season_name} ${form.season_year}`,
      });
      if (!result.success) {
        setSubmitError(result.error || 'Submission failed.');
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="submit-page success-page">
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
        </div>
        <div className="success-body">
          <div className="loading-spinner" style={{ margin: '40px auto' }}></div>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: '16px' }}>Verifying access...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="submit-page success-page">
        {/* Header */}
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
          <button className="sland-back-btn" onClick={() => navigate("/standouts")}>
            Standouts
          </button>
        </div>

        {/* Main content */}
        <div className="success-body">
          {/* Checkmark */}
          <div className="success-check-circle">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1 className="success-heading">Standout submitted!</h1>

          <div className="success-divider" />

          <p className="success-sub">Your submission is under review.</p>

          <button
            className="success-resubmit-btn"
            onClick={() => { setForm(INITIAL_FORM); setConfirmPermission(false); setConfirmAuthorized(false); setSubmitted(false); }}
          >
            Submit Another Athlete
          </button>

          <button className="success-view-btn" onClick={() => navigate("/standouts")}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            View Standouts
          </button>
        </div>

        {/* Footer */}
        <div className="success-footer">
          <div className="sland-footer-left">
            <img src="/assets/images/logo.png" alt="LegacyStat" className="sland-footer-logo" />
          </div>
          <p className="sland-footer-copy">© 2026 LegacyStat. All rights reserved.</p>
          <div className="sland-footer-links">
            <a href="#" className="sland-footer-link">Privacy Policy</a>
            <span className="sland-footer-sep">·</span>
            <a href="#" className="sland-footer-link">Terms of Service</a>
          </div>
        </div>
      </div>
    );
  }

  // ── Org Step: Select ──
  if (orgStep === 'select') {
    const approvedOrgs = userOrganizations.filter(o => o.status === 'approved');
    const pendingOrgs = userOrganizations.filter(o => o.status === 'pending');

    return (
      <div className="submit-page success-page">

        {/* Join Request Success Modal */}
        {joinSuccessOrg && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#0e0e0e', border: '1px solid rgba(243,177,41,0.25)', borderRadius: '16px', padding: '36px 32px', maxWidth: '400px', width: '90%', textAlign: 'center' }}>
              <div style={{ width: '56px', height: '56px', background: 'rgba(243,177,41,0.12)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f3b129" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>Request Sent!</h2>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
                Your request to join <span style={{ color: '#f3b129', fontWeight: '600' }}>{joinSuccessOrg}</span> has been sent. You'll be notified once it's approved.
              </p>
              <button
                onClick={() => setJoinSuccessOrg(null)}
                style={{ background: '#f3b129', color: '#000', border: 'none', borderRadius: '10px', padding: '11px 32px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}
              >
                Got it
              </button>
            </div>
          </div>
        )}
        {/* Desktop topbar */}
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={() => navigate('/standouts')} style={{ background: '#f3b129', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Standouts</button>
            {currentUser ? (
              <button onClick={handleLogout} style={{ background: '#f3b129', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Log Out</button>
            ) : (
              <button onClick={() => navigate('/standouts/login')} style={{ background: '#f3b129', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Login</button>
            )}
          </div>
        </div>

        {/* Mobile topbar */}
        <div className="mobile-org-header">
          <img src="/assets/images/legacy logo plain.png" alt="LegacyStat" className="success-logo" />
          <div className="mobile-org-header-btns">
            <button className="mobile-org-header-btn" onClick={() => navigate('/standouts')}>Standouts</button>
            {currentUser ? (
              <button className="mobile-org-header-btn" onClick={handleLogout}>Log Out</button>
            ) : (
              <button className="mobile-org-header-btn" onClick={() => navigate('/standouts/login')}>Login</button>
            )}
          </div>
        </div>
        <div className="success-body" style={{ alignItems: 'center', justifyContent: 'flex-start', paddingTop: '90px' }}>
          <div style={{ width: '100%', maxWidth: '520px' }}>

            {/* Page title */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h1 className="success-heading" style={{ fontSize: '22px', marginBottom: '8px' }}>
                {currentUser ? 'Select Your Organization' : 'Search Organization'}
              </h1>
              <p className="success-sub">
                {currentUser ? 'Select an organization to submit standouts.' : 'Search for your league, school, or organization to continue.'}
              </p>
            </div>

            {/* Search bar — standalone above the card */}
            <div style={{ marginBottom: '16px', position: 'relative', zIndex: 10 }}>
              <OrgSearchDropdown
                value={selectedOrg}
                onChange={handleOrgSelect}
                placeholder="Search your organization..."
              />
              {selectedOrg && (
                currentUser ? (
                  // Logged in user
                  !approvedOrgs.find(o => o.id === selectedOrg.id) && (
                    <button
                      className="submit-btn"
                      style={{ marginTop: '10px', width: '100%', height: '44px', fontSize: '16px', fontWeight: '700', opacity: requestSubmitting ? 0.75 : 1 }}
                      onClick={() => handleRequestToJoin(selectedOrg)}
                      disabled={requestSubmitting}
                    >
                      {requestSubmitting ? 'Sending request...' : 'Request to Join'}
                    </button>
                  )
                ) : (
                  // Not logged in user
                  <button
                    className="submit-btn"
                    style={{ marginTop: '10px', width: '100%', height: '44px', fontSize: '16px', fontWeight: '700' }}
                    onClick={() => navigate('/standouts/login')}
                  >
                    Login to Request
                  </button>
                )
              )}
            </div>

            {/* Tabbed card — My Organizations + Pending + All Organizations */}
            {currentUser && (
            <div style={{ background: '#0e0e0e', border: '1px solid rgba(243,177,41,0.15)', borderRadius: '16px' }}>

              {/* Tab bar */}
              <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
                {(currentUser ? [
                  { key: 'my', label: 'My Organizations' },
                  { key: 'pending', label: `Pending${pendingOrgs.length > 0 ? ` (${pendingOrgs.length})` : ''}` },
                  { key: 'all', label: 'Organizations' }
                ] : [
                  { key: 'all', label: 'Organizations' }
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setOrgTab(tab.key)}
                    style={{
                      flex: 1, padding: '14px', border: 'none', cursor: 'pointer',
                      background: 'transparent',
                      color: orgTab === tab.key ? '#fff' : 'rgba(255,255,255,0.4)',
                      fontWeight: orgTab === tab.key ? '600' : '400',
                      fontSize: '14px', fontFamily: 'Inter, sans-serif',
                      borderBottom: orgTab === tab.key ? '2px solid #f3b129' : '2px solid transparent',
                      transition: 'all 0.2s',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ padding: '24px', textAlign: 'left' }}>

                {/* MY ORGANIZATIONS tab */}
                {orgTab === 'my' && (
                  <div className="org-list-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto' }}>
                    {approvedOrgs.length > 0 ? approvedOrgs.map((org) => (
                      <div
                        key={org.id}
                        onClick={() => handleOrgContinue(org)}
                        style={{ padding: '14px 16px', background: 'rgba(243,177,41,0.05)', border: '1px solid rgba(243,177,41,0.2)', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(243,177,41,0.1)'; e.currentTarget.style.borderColor = 'rgba(243,177,41,0.4)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(243,177,41,0.05)'; e.currentTarget.style.borderColor = 'rgba(243,177,41,0.2)'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '3px' }}>{org.name}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                              {org.sport && `${org.sport} • `}{org.city && org.state ? `${org.city}, ${org.state}` : org.city || org.state || ''}
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOrgContinue(org); }}
                            style={{ padding: '7px 16px', background: '#f3b129', color: '#000', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Select →
                          </button>
                        </div>
                      </div>
                    )) : (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
                        No approved organizations yet.
                      </p>
                    )}
                  </div>
                )}

                {/* PENDING tab */}
                {orgTab === 'pending' && (
                  <div className="org-list-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto' }}>
                    {pendingOrgs.length > 0 ? pendingOrgs.map((org) => (
                      <div key={org.id} style={{ padding: '14px 16px', background: 'rgba(243,177,41,0.04)', border: '1px solid rgba(243,177,41,0.15)', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '3px' }}>{org.name}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                              {org.sport && `${org.sport} • `}{org.city && org.state ? `${org.city}, ${org.state}` : org.city || org.state || ''}
                            </div>
                          </div>
                          <div style={{ padding: '5px 12px', background: 'rgba(243,177,41,0.12)', color: '#f3b129', border: '1px solid rgba(243,177,41,0.25)', borderRadius: '5px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            Pending
                          </div>
                        </div>
                      </div>
                    )) : (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
                        No pending requests.
                      </p>
                    )}
                  </div>
                )}

                {/* ALL ORGANIZATIONS tab */}
                {orgTab === 'all' && (
                  <div className="org-list-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto' }}>
                    {allOrganizations.length > 0 ? allOrganizations.map((org) => {
                      const isApproved = currentUser && approvedOrgs.find(o => o.id === org.id);
                      const isPending = currentUser && pendingOrgs.find(o => o.id === org.id);

                      return (
                        <div
                          key={org.id}
                          onClick={() => {
                            if (!currentUser) {
                              navigate('/standouts/login');
                            } else if (isApproved) {
                              handleOrgContinue(org);
                            }
                          }}
                          style={{
                            padding: '14px 16px',
                            background: isApproved ? 'rgba(243,177,41,0.05)' : 'rgba(243,177,41,0.02)',
                            border: isApproved ? '1px solid rgba(243,177,41,0.2)' : '1px solid rgba(243,177,41,0.1)',
                            borderRadius: '10px',
                            cursor: !currentUser || isApproved ? 'pointer' : 'default',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!currentUser || isApproved) {
                              e.currentTarget.style.background = isApproved ? 'rgba(243,177,41,0.1)' : 'rgba(243,177,41,0.05)';
                              e.currentTarget.style.borderColor = 'rgba(243,177,41,0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = isApproved ? 'rgba(243,177,41,0.05)' : 'rgba(243,177,41,0.02)';
                            e.currentTarget.style.borderColor = isApproved ? 'rgba(243,177,41,0.2)' : 'rgba(243,177,41,0.1)';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontSize: '15px', fontWeight: '600', color: '#fff', marginBottom: '3px' }}>{org.name}</div>
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>
                                {org.sport && `${org.sport} • `}{org.city && org.state ? `${org.city}, ${org.state}` : org.city || org.state || ''}
                              </div>
                            </div>
                            {!currentUser ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate('/standouts/login'); }}
                                style={{ padding: '7px 16px', background: '#f3b129', color: '#000', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                Login to Join →
                              </button>
                            ) : isApproved ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleOrgContinue(org); }}
                                style={{ padding: '7px 16px', background: '#f3b129', color: '#000', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                Select →
                              </button>
                            ) : isPending ? (
                              <div style={{ padding: '5px 12px', background: 'rgba(243,177,41,0.12)', color: '#f3b129', border: '1px solid rgba(243,177,41,0.25)', borderRadius: '5px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                Pending
                              </div>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRequestToJoin(org); }}
                                style={{ padding: '7px 16px', background: 'rgba(243,177,41,0.15)', color: '#f3b129', border: '1px solid rgba(243,177,41,0.3)', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                Request to Join
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
                        No organizations available.
                      </p>
                    )}
                  </div>
                )}

              </div>
            </div>
            )}

            {/* Request org link — below the card */}
            <div style={{ textAlign: 'left', marginTop: '16px' }}>
              <button
                onClick={() => {
                  if (!currentUser) {
                    navigate('/standouts/login');
                  } else {
                    setOrgStep('request');
                  }
                }}
                style={{ background: 'none', border: 'none', color: '#f3b129', fontSize: '14px', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Don't see your org? Request it
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── Org Step: Request ──
  if (orgStep === 'request') {
    return (
      <div className="submit-page success-page">
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
          <button className="back-btn" onClick={() => setOrgStep('select')}>← Back</button>
        </div>
        <div className="success-body">
        <div style={{ maxWidth: '520px', width: '100%', textAlign: 'left' }}>
          <h1 className="success-heading" style={{ fontSize: '22px', marginBottom: '8px' }}>Request Your Organization</h1>
          <p className="success-sub" style={{ marginBottom: '24px' }}>Fill in your organization details. We'll review and approve it shortly.</p>

          {requestError && <p style={{ color: '#ff4d4d', fontSize: '13px', marginBottom: '12px' }}>{requestError}</p>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
            <label className="field-label" style={{ width: '100%' }}>
              Organization Name <span className="required">*</span>
              <input value={requestForm.name} onChange={(e) => setRequestForm((p) => ({ ...p, name: e.target.value }))} placeholder="Enter organization name" />
            </label>
            <label className="field-label" style={{ width: '100%' }}>
              Sport
              <select value={requestForm.sport} onChange={(e) => setRequestForm((p) => ({ ...p, sport: e.target.value }))}>
                {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <CityAutocomplete
              city={requestForm.city}
              state={requestForm.state}
              onCityChange={(val) => setRequestForm((p) => ({ ...p, city: val }))}
              onStateChange={(val) => setRequestForm((p) => ({ ...p, state: val }))}
            />
            <label className="field-label" style={{ width: '100%' }}>
              Contact Name <span className="required">*</span>
              <input value={requestForm.contact_name} onChange={(e) => setRequestForm((p) => ({ ...p, contact_name: e.target.value }))} placeholder="Full name" />
            </label>
            <label className="field-label" style={{ width: '100%' }}>
              Contact Email <span className="required">*</span>
              <input type="email" value={requestForm.contact_email} onChange={(e) => setRequestForm((p) => ({ ...p, contact_email: e.target.value }))} placeholder="you@example.com" />
            </label>
            <label className="field-label" style={{ width: '100%' }}>
              Phone Number <span className="required">*</span>
              <input value={requestForm.phone} onChange={(e) => setRequestForm((p) => ({ ...p, phone: e.target.value }))} placeholder="e.g. (555) 123-4567" />
            </label>
            <label className="field-label" style={{ width: '100%' }}>
              Website or Instagram <span className="optional">(optional)</span>
              <input value={requestForm.website} onChange={(e) => setRequestForm((p) => ({ ...p, website: e.target.value }))} placeholder="https:// or @handle" />
            </label>
          </div>

          <button className="submit-btn" style={{ marginTop: '24px' }} disabled={requestSubmitting} onClick={handleOrgRequest}>
            {requestSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // ── Org Step: Pending ──
  if (orgStep === 'pending') {
    return (
      <div className="submit-page success-page">
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
          <button className="back-btn" onClick={() => navigate('/standouts')}>← Standouts</button>
        </div>
        <div className="success-body">
          <div className="success-check-circle" style={{ background: 'rgba(216,161,52,0.1)', border: '1px solid rgba(216,161,52,0.3)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D8A134" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <h1 className="success-heading">Request Submitted!</h1>
          <div className="success-divider" />
          <p className="success-sub">Your organization request is under review. Once approved, you'll be able to submit standouts. Check back shortly.</p>
          <button className="success-view-btn" onClick={() => navigate('/standouts')}>
            View Standouts
          </button>
        </div>
      </div>
    );
  }

  if (isSubmissionLocked()) {
    return (
      <div className="submit-page success-page">
        <div className="success-topbar">
          <img src="/assets/images/logo.png" alt="LegacyStat" className="success-logo" />
          <button className="sland-back-btn" onClick={() => navigate('/standouts')}>Standouts</button>
        </div>
        <div className="success-body">
          <div className="success-check-circle" style={{ background: 'rgba(216,161,52,0.1)', border: '1px solid rgba(216,161,52,0.3)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D8A134" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h1 className="success-heading">Submissions Closed</h1>
          <div className="success-divider" />
          <p className="success-sub">Submissions close Sunday at 10pm and reopen Monday at 5pm after the weekly release goes live.</p>
          <button className="success-view-btn" onClick={() => navigate('/standouts')}>
            View This Week's Standouts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-page">
      <div className="submit-container">

        {/* ─────────────── LEFT PANEL ─────────────── */}
        <div className="left-panel">

          <button className="back-btn" onClick={() => navigate("/standouts")}>
            ← Back to Standouts
          </button>

          <h1>Submit Standouts</h1>
          <p className="subtitle">Nominate athletes for this week's Standouts.</p>
          <div className="review-note">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
            All submissions are reviewed before publishing.
          </div>

          {/* ── Section 1: Organization ── */}
          <div className="card">
            <div className="card-header">
              <span className="section-number">1</span>
              <img src="/assets/images/standout-organization-information.png" alt="" className="section-icon" />
              <h3>Organization Information</h3>
            </div>

            {/* Selected org display */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(216,161,52,0.08)', border: '1px solid rgba(216,161,52,0.25)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
              <div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{selectedOrg?.name}</span>
                <span style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                  {[selectedOrg?.sport, selectedOrg?.city && selectedOrg?.state ? `${selectedOrg.city}, ${selectedOrg.state}` : ''].filter(Boolean).join(' · ')}
                </span>
              </div>
              <button type="button" onClick={() => setOrgStep('select')} style={{ background: 'none', border: 'none', color: '#D8A134', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                Change
              </button>
            </div>

            <label className="field-label">
              Season <span className="required">*</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select name="season_name" value={form.season_name} onChange={update} style={{ flex: 1 }}>
                  {SEASONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div className="week-picker-number-wrap" style={{ width: '90px' }}>
                  <input
                    name="season_year"
                    type="number"
                    value={form.season_year}
                    onChange={update}
                    min="2020"
                    max="2099"
                    className="week-picker-input week-picker-number"
                  />
                  <div className="week-picker-spinners">
                    <button type="button" className="week-picker-spin" onClick={() => setForm((p) => ({ ...p, season_year: Math.min(2099, (p.season_year || 2026) + 1) }))}>
                      <img src="/assets/images/arrow-up-icon.png" alt="up" className="week-picker-spin-icon" />
                    </button>
                    <button type="button" className="week-picker-spin" onClick={() => setForm((p) => ({ ...p, season_year: Math.max(2020, (p.season_year || 2026) - 1) }))}>
                      <img src="/assets/images/arrow-down-icon.png" alt="down" className="week-picker-spin-icon" />
                    </button>
                  </div>
                </div>
              </div>
            </label>

            {/* Week & Date Fields */}
            <WeekPicker
              week={form.week}
              startDate={form.startDate}
              dateRange={form.dateRange}
              onChange={({ week, dateRange, startDate }) =>
                setForm((prev) => ({ ...prev, week, dateRange, startDate }))
              }
            />

            {/* Logged-in user info display */}
            <div style={{
              background: 'rgba(216,161,52,0.05)',
              border: '1px solid rgba(216,161,52,0.2)',
              borderRadius: '10px',
              padding: '16px',
              marginTop: '16px',
              marginBottom: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(216,161,52,0.15)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D8A134" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Submitting As
                  </div>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: form.yourName ? '#fff' : 'rgba(255,255,255,0.4)',
                    marginBottom: '4px'
                  }}>
                    {form.yourName || 'Loading user information...'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: form.email ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.4)'
                  }}>
                    {form.email || 'Loading email...'}
                  </div>
                </div>
              </div>
            </div>

            <p className="field-note">This is how we'll contact you about your submission.</p>
          </div>

          {/* ── Section 2: Athlete Info ── */}
          <div className="card">
            <div className="card-header">
              <span className="section-number">2</span>
              <img src="/assets/images/standout-athlete-information.png" alt="" className="section-icon" />
              <h3>Athlete Information</h3>
            </div>

            {/* Existing athlete selector */}
            {existingAthletes.length > 0 && (
              <div style={{ marginBottom: '20px' }} ref={athleteDropdownRef}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Select Existing Athlete
                </p>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(216,161,52,0.35)', borderRadius: '10px', padding: '0 12px', cursor: 'text' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontFamily: 'inherit', fontSize: '14px', padding: '12px 0', cursor: 'pointer' }}
                      placeholder="Search existing athletes..."
                      value={athleteQuery}
                      onChange={(e) => { setAthleteQuery(e.target.value); setAthleteDropdownOpen(true); }}
                      onFocus={() => setAthleteDropdownOpen(true)}
                      readOnly={false}
                    />
                    {athleteQuery && (
                      <button type="button" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer', padding: 0 }}
                        onClick={(e) => { e.stopPropagation(); setAthleteQuery(''); setAthleteDropdownOpen(false); }}>×</button>
                    )}
                  </div>
                  {athleteDropdownOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#1a1a1a', border: '1px solid rgba(216,161,52,0.3)', borderRadius: '10px', zIndex: 100, maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                      {existingAthletes
                        .filter((a) => !athleteQuery || a.athlete_name.toLowerCase().includes(athleteQuery.toLowerCase()))
                        .map((a, i) => (
                          <div
                            key={i}
                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setForm((prev) => ({
                                ...prev,
                                athleteName: a.athlete_name || '',
                                teamName: a.team_name || '',
                                sport: a.sport || prev.sport,
                                level: a.level || prev.level,
                                division: a.division || prev.division,
                              }));
                              setAthleteQuery(a.athlete_name);
                              setAthleteDropdownOpen(false);
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(216,161,52,0.08)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#fff' }}>{a.athlete_name}</span>
                            <span style={{ display: 'block', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                              {[a.team_name, a.sport, a.level].filter(Boolean).join(' · ')}
                            </span>
                          </div>
                        ))}
                      {existingAthletes.filter((a) => !athleteQuery || a.athlete_name.toLowerCase().includes(athleteQuery.toLowerCase())).length === 0 && (
                        <div style={{ padding: '16px', fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>No athletes found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="row">
              <label className="field-label">
                Athlete Full Name <span className="required">*</span>
                <input name="athleteName" value={form.athleteName} placeholder="Enter athlete's full name" onChange={update} />
              </label>
              <label className="field-label">
                Team Name <span className="required">*</span>
                <input name="teamName" value={form.teamName} placeholder="Enter team name" onChange={update} />
              </label>
            </div>

            <div className="row-three">
              <label className="field-label">
                Sport <span className="required">*</span>
                <select name="sport" value={form.sport} onChange={update}>
                  {SPORTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="field-label">
                Level <span className="required">*</span>
                <select name="level" value={form.level} onChange={update}>
                  <optgroup label="Grade Levels">
                    {LEVELS_GRADE.map((l) => <option key={l} value={l}>{l}</option>)}
                  </optgroup>
                  <optgroup label="Age Levels">
                    {LEVELS_AGE.map((l) => <option key={l} value={l}>{l}</option>)}
                  </optgroup>
                  <optgroup label="School Levels">
                    {LEVELS_SCHOOL.map((l) => <option key={l} value={l}>{l}</option>)}
                  </optgroup>
                </select>
              </label>
              <label className="field-label">
                Division <span className="required">*</span>
                <select name="division" value={form.division} onChange={update}>
                  {DIVISIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* ── Section 3: Award ── */}
          <div className="card">
            <div className="card-header">
              <span className="section-number">3</span>
              <img src="/assets/images/standout-award-recognition.png" alt="" className="section-icon" />
              <h3>Award / Recognition</h3>
            </div>

            <label className="field-label">
              Award Type <span className="required">*</span>
              <select name="award" value={form.award} onChange={update}>
                {AWARDS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>

            <p className="field-note">Select the award or recognition earned by this athlete.</p>
          </div>

          {/* ── Section 4: Key Stats ── */}
          <div className="card">
            <div className="card-header">
              <span className="section-number">4</span>
              <img src="/assets/images/standout-key-stats.png" alt="" className="section-icon" />
              <h3>Key Stats <span className="section-sub">(Max 3)</span></h3>
            </div>

            <p className="field-note" style={{ marginBottom: 16 }}>
              Highlight the athlete's performance with up to 3 key stats.
            </p>

            <div className="stats-grid">
              <div className="stats-head">
                <span />
                <span>Value</span>
                <span>Label</span>
              </div>
              <label className="stat-row">
                <span className="stat-tag">Stat 1 <span className="required">*</span></span>
                <input name="stat1" value={form.stat1} onChange={update} placeholder="e.g. 5" />
                <input name="label1" value={form.label1} onChange={update} placeholder="e.g. TDs" />
              </label>
              <label className="stat-row">
                <span className="stat-tag">Stat 2 <span className="optional">(Optional)</span></span>
                <input name="stat2" value={form.stat2} onChange={update} placeholder="Value" />
                <input name="label2" value={form.label2} onChange={update} placeholder="Label" />
              </label>
              <label className="stat-row">
                <span className="stat-tag">Stat 3 <span className="optional">(Optional)</span></span>
                <input name="stat3" value={form.stat3} onChange={update} placeholder="Value" />
                <input name="label3" value={form.label3} onChange={update} placeholder="Label" />
              </label>
            </div>
          </div>

          {/* ── Athlete Photo ── */}
          <div className="card">
            <div className="card-header">
              <img src="/assets/images/standout-athlete-photo.png" alt="" className="section-icon" />
              <h3>Athlete Photo</h3>
            </div>
            <p className="field-note" style={{ marginBottom: 6 }}>
              Upload a clear photo of the athlete. This will appear on the Standout card.
            </p>
            <p className="field-note crop-required-note" style={{ marginBottom: 14 }}>
              Crop selection should be required before submission.
            </p>

            {form.image ? (
              <div className="upload-preview">
                <img src={form.image} alt="Athlete preview" className="upload-preview-img" style={{ opacity: uploadingPhoto ? 0.5 : 1 }} />
                {uploadingPhoto ? (
                  <p style={{ color: '#D8A134', fontSize: '13px', marginTop: '8px', textAlign: 'center' }}>Uploading photo...</p>
                ) : (
                  <label className="upload-change-btn">
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={imageUpload} />
                    Change Photo
                  </label>
                )}
              </div>
            ) : (
              <label className="upload-box">
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={imageUpload} />
                <div className="upload-content">
                  <img src="/assets/images/standout-upload-icon.png" alt="Upload" className="upload-icon-img" />
                  <strong>Click to upload or drag and drop</strong>
                  <span>JPG, PNG or WEBP (Max 10MB) • 4:5 ratio</span>
                </div>
              </label>
            )}
          </div>

          {uploadError && (
            <p style={{ color: '#ff4d4d', fontSize: '13px', marginBottom: '12px' }}>{uploadError}</p>
          )}
          {submitError && (
            <p style={{ color: '#ff4d4d', fontSize: '13px', marginBottom: '12px' }}>{submitError}</p>
          )}

          <button className="secondary-btn">+ Add Another Athlete</button>

          <div className="consent-checks">
            <label className="consent-check">
              <input
                type="checkbox"
                className="consent-check-input"
                checked={confirmPermission}
                onChange={(e) => setConfirmPermission(e.target.checked)}
              />
              <span className="consent-check-box">
                {confirmPermission && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2 6 5 9 10 3" />
                  </svg>
                )}
              </span>
              <span className="consent-check-label">
                I confirm I have permission to upload this content and share this athlete's information
              </span>
            </label>

            <label className="consent-check">
              <input
                type="checkbox"
                className="consent-check-input"
                checked={confirmAuthorized}
                onChange={(e) => setConfirmAuthorized(e.target.checked)}
              />
              <span className="consent-check-box">
                {confirmAuthorized && (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="#111" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="2 6 5 9 10 3" />
                  </svg>
                )}
              </span>
              <span className="consent-check-label">
                I am a coach, parent/guardian, or authorized representative
              </span>
            </label>
          </div>

          <button className="submit-btn" disabled={uploadingPhoto || submitting || cropModalOpen || !confirmPermission || !confirmAuthorized} onClick={handleSubmit}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
            {submitting ? 'Submitting...' : 'Submit Standouts'}
          </button>

          <p className="disclaimer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            By submitting, you confirm the information provided is accurate.
          </p>
        </div>

        {/* ─────────────── RIGHT PANEL ─────────────── */}
        <div className="right-panel">

          {/* pushes preview down to align with the h1 (back-btn height + margin) */}
          <div className="preview-spacer" />

          {/* ── Preview Card ── */}
          <div className="card preview-section">
            <h4 className="preview-label">Preview</h4>
            <p className="preview-desc">This is how your approved Standout will appear.</p>

            {/* Exact same sd-card used on /standouts/athlete/:id */}
            <div className="sd-card">

              <div className="sd-card-header">
                <h1 className="sd-week-heading">{form.week ? `WEEK ${form.week} STANDOUT` : 'WEEK 1 STANDOUT'}</h1>
                <div className="sd-header-divider">
                  <span className="sd-header-line" />
                  <span className="sd-header-star">★</span>
                  <span className="sd-header-line" />
                </div>
                <div className="sd-header-sub">
                  <span className="sd-sub-side sd-sub-left">{form.season_name} {form.season_year}</span>
                  <span className="sd-header-dot">•</span>
                  <span className="sd-sub-side sd-sub-right">{form.orgName || "Youth Flag"}</span>
                </div>
              </div>

              <div className="sd-photo-wrap">
                <img src={form.image || FALLBACK_IMG} alt="Athlete" className="sd-photo" />
                <div className="sd-photo-gradient" />
              </div>

              <div className="sd-body">
                <h2 className="sd-name">{(form.athleteName || "Jaylen Carter").toUpperCase()}</h2>

                <div className="sd-award-row">
                  <span className="sd-award-line" />
                  <div className="sd-award-badge">
                    <span className="sd-award-text">{(form.award || "MVP").toUpperCase()}</span>
                  </div>
                  <span className="sd-award-line sd-award-line--right" />
                </div>
                <p className="sd-subtitle">
                  <span className="sd-subtitle-gold">{form.teamName || "Team Legacy"}</span>
                  <span className="sd-dot"> • </span>
                  {form.sport}
                  {form.level && (
                    <>
                      <span className="sd-dot"> • </span>
                      <span>{form.level}</span>
                    </>
                  )}
                  {form.division && (
                    <>
                      <span className="sd-dot"> • </span>
                      <span className="sd-subtitle-gold">{form.division}</span>
                    </>
                  )}
                </p>

                <div className="sd-divider" />
                <p className="sd-date">{form.dateRange ? `Week of ${form.dateRange}` : 'Week of Feb 10–16, 2026'}</p>
                <div className="sd-divider" />

                <div className="sd-stats-row">
                  {previewStats.map((s, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <div className="sd-stat-sep" />}
                      <div className="sd-stat">
                        <span className="sd-stat-value">{s.v}</span>
                        <span className="sd-stat-label">{s.l}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                <div className="sd-divider" />

                <button className="sd-share-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                    <polyline points="16 6 12 2 8 6"/>
                    <line x1="12" y1="2" x2="12" y2="15"/>
                  </svg>
                  SHARE
                </button>
              </div>
            </div>
          </div>

          {/* ── Submission Tips ── */}
          <div className="side-card">
            <h3 className="tips-heading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f3b129" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Submission Tips
            </h3>
            <div className="tips-list">
              {TIPS.map((tip, i) => (
                <div key={i} className="tip-item">
                  <div className="tip-icon-wrap">{tip.svg}</div>
                  <div className="tip-text">
                    <strong>{tip.title}</strong>
                    <span>{tip.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>


        </div>
      </div>

      {/* ── Crop Modal ── */}
      {cropModalOpen && (
        <div className="crop-modal-overlay">
          <div className="crop-modal">
            <h3 className="crop-modal-title">Crop Photo</h3>
            <p className="crop-modal-desc">Drag to reposition • Pinch or scroll to zoom • 4:5 ratio</p>
            <div className="crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={4 / 5}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="crop-zoom-row">
              <span className="crop-zoom-label">Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="crop-zoom-slider"
              />
            </div>
            <div className="crop-modal-actions">
              <button className="crop-cancel-btn" onClick={handleCropCancel}>Cancel</button>
              <button className="crop-done-btn" onClick={handleCropDone}>Crop &amp; Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
